/**
 * Cast headshots — the web half of Unity's `setCharacterHeadshots` push
 * (TimelineWebViewPanel.SendCharacterHeadshotsToWebView).
 *
 * Unity sends this AFTER every project push to this WebView (fresh open and the `refresh:true`
 * re-push) with whatever `char_*.png` files the project's thumbnail cache already holds, and again
 * at the end of a generation pass that wrote at least one new file. The ordering is the contract:
 * the web clears its map on a fresh `setSqlitePath` (features/timeline.ts), so a set that arrived
 * BEFORE the push would be wiped by it.
 *
 * Validation is defensive because the payload is hand-built JSON on the Unity side (JsonEscape +
 * string concatenation, the `SendShotFullImageReply` idiom) rather than a serialized DTO: a
 * malformed entry is dropped, never allowed to poison the map with `undefined` keys or a non-string
 * src that React would hand to an <img>. An entry-level drop rather than a message-level reject,
 * so one bad row cannot cost the user every other portrait.
 */

import type { HandlerMap } from '../dispatch';
import { useCastHeadshotStore } from '@/store/castHeadshotStore.ts';
import type { CastHeadshotEntry } from '@/store/castHeadshotStore.ts';

// ─── Message types ───────────────────────────────────────────────────────

export interface SetCharacterHeadshotsMessage {
  type: 'setCharacterHeadshots';
  /** The FULL set for the open project — replace, never merge. An empty array is meaningful: it
   *  means no headshot exists on disk yet, and the rows must fall back to the placeholder. */
  payload: { headshots: Array<{ characterId: number; url: string }> };
}

export type CastHeadshotsMessage = SetCharacterHeadshotsMessage;

// ─── Inbound handler ─────────────────────────────────────────────────────

export const castHeadshotsHandlers = {
  setCharacterHeadshots: (msg: SetCharacterHeadshotsMessage) => {
    const raw = msg.payload?.headshots;
    if (!Array.isArray(raw)) return; // not "clear the map": a shapeless message says nothing at all
    const entries: CastHeadshotEntry[] = [];
    for (const e of raw) {
      const id = (e as { characterId?: unknown } | null)?.characterId;
      const url = (e as { url?: unknown } | null)?.url;
      // `> 0` and not merely finite: 0 is the `characters.characterid` default for "no row", and a
      // negative id is the optimistic-duplicate temp id no file could ever be named after.
      if (typeof id !== 'number' || !Number.isFinite(id) || id <= 0) continue;
      if (typeof url !== 'string' || url === '') continue;
      entries.push({ characterId: id, url });
    }
    useCastHeadshotStore.getState().replaceAll(entries);
  },
} satisfies HandlerMap;
